/**
 * SettingsPanel.jsx — Settings management with 10 tabs
 *
 * Tabs:
 * 1. Profile — user info, avatar, password (ProfileTab component)
 * 2. Company — company info, homebase, logo
 * 3. Appearance — theme, accent color, density, sidebar
 * 4. Quotes — prefix, numbering, deposit, validity, T&Cs
 * 5. Finance — invoice settings, split defaults, XP
 * 6. Email Templates — 7 customizable templates
 * 7. Team — roster, crew rates, permissions (TeamTab component)
 * 8. Notifications — per-user notification prefs
 * 9. Integrations — Xero, Google Calendar, etc. (IntegrationsTab component)
 * 10. Data — export/import (DataTab component)
 * 11. Trash — deleted jobs/invoices, restorable for 30 days (TrashTab component)
 * 12. Feedback — bug / idea box inbox (FeedbackTab component)
 */

import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSettings, useUpdateSettings } from '../../hooks/useSettings';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../layout/Toast';
import AddressAutocomplete from '../shared/AddressAutocomplete';
import { cn } from '../../lib/utils';
import { useMobile } from '../../hooks/useMobile';
import { useRBAC } from '../../hooks/useRBAC';
import { useFocus } from '../../hooks/useFocus';
import { TEMPLATE_TYPES, MERGE_TAGS } from '../../lib/emailTemplates';
import ProfileTab from './ProfileTab';
import QuoteTermsTab from './QuoteTermsTab';
import TeamTab from './TeamTab';
import DataTab from './DataTab';
import IntegrationsTab from './IntegrationsTab';
import RoleMatrixBuilder from './RoleMatrixBuilder';
import TrashTab from './TrashTab';
import FeedbackTab from './FeedbackTab';
import ActionLog from '../actionlog/ActionLog';

export default function SettingsPanel() {
  const { data: settings, isLoading } = useSettings();
  const updateSettings = useUpdateSettings();
  const { profile } = useAuth();
  const { toast } = useToast();
  const { isMobile } = useMobile();
  const { hasAction, hasAccess, hasToolAccess, isSuperAdmin } = useRBAC();
  const { isHidden } = useFocus();

  // ── Tabs ─────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('profile');
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t) { setActiveTab(t); searchParams.delete('tab'); setSearchParams(searchParams, { replace: true }); }
  }, [searchParams, setSearchParams]);

  // ── Form state (company-level settings) ──────────────────────────────────
  const [formData, setFormData] = useState({
    // Company
    companyName: '',
    companyAddress: '',
    companyPhone: '',
    companyEmail: '',
    companyWeb: '',
    homebaseAddress: '',
    logoUrl: '',
    // Appearance
    accentColor: '#8B5CF6',
    theme: 'dark',
    sidebarDefault: 'expanded',
    density: 'comfortable',
    // Quotes
    quotePrefix: 'Q',
    quoteStart: '1000',
    depositPct: '50',
    quoteValidity: '30',
    payTerms: 'Net 30',
    finalTermsDays: '0',
    // Finance
    invoicePrefix: 'INV',
    invoiceStart: '1',
    splitCompany: '40',
    splitOriginator: '10',
    splitProduction: '25',
    splitPostProduction: '25',
    defaultCalendarView: 'month',
    defaultCalendarScope: '',
    archivePaidDays: '3',
    archiveVoidDays: '3',
    mileageRate: '0.70',
    xpRoleBase: '50',
    xpEngagement: '1',
    // Outreach
    yourName: '',
    portfolioLink: '',
    videoLink: '',
  });

  // ── Notification prefs (per-user, stored in settings.userPrefs[userId]) ──
  const [notifPrefs, setNotifPrefs] = useState({
    emailJobAssigned: true,
    emailInvoicePaid: true,
    emailQuoteAccepted: true,
    emailPaymentReceived: true,
    emailWeeklyDigest: false,
    emailDailyDigest: false,
  });

  // ── Email template state ────────────────────────────────────────────────
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [templateSubject, setTemplateSubject] = useState('');
  const [templateBody, setTemplateBody] = useState('');
  // ── Load settings into form ──────────────────────────────────────────────
  useEffect(() => {
    if (settings?.data) {
      const d = settings.data;
      setFormData((prev) => ({
        ...prev,
        // Company
        companyName: d.companyName || '',
        companyAddress: d.companyAddress || '',
        companyPhone: d.companyPhone || '',
        companyEmail: d.companyEmail || '',
        companyWeb: d.companyWeb || '',
        homebaseAddress: d.homebaseAddress || '',
        logoUrl: d.logoUrl || '',
        // Appearance
        accentColor: d.accentColor || '#8B5CF6',
        theme: d.theme || 'dark',
        sidebarDefault: d.sidebarDefault || 'expanded',
        density: d.density || 'comfortable',
        // Quotes
        quotePrefix: d.quotePrefix || 'Q',
        quoteStart: String(d.quoteStart || '1000'),
        depositPct: String(d.depositPct || '50'),
        quoteValidity: String(d.quoteValidity || '30'),
        payTerms: d.payTerms || 'Net 30',
        finalTermsDays: String(d.finalTermsDays ?? '0'),
        // Outreach
        yourName: d.yourName || '',
        portfolioLink: d.portfolioLink || '',
        videoLink: d.videoLink || '',
        // Finance
        invoicePrefix: d.invoicePrefix || 'INV',
        invoiceStart: String(d.invoiceStart || '1'),
        splitCompany: String(d.splitCompany ?? '40'),
        splitOriginator: String(d.splitOriginator ?? '10'),
        splitProduction: String(d.splitProduction ?? '25'),
        splitPostProduction: String(d.splitPostProduction ?? '25'),
        defaultCalendarView: (profile?.id && d.userPrefs?.[profile.id]?.calendarView) || d.defaultCalendarView || 'month',
        defaultCalendarScope: (profile?.id && d.userPrefs?.[profile.id]?.calendarScope) || '',
        archivePaidDays: String(d.archivePaidDays || '3'),
        archiveVoidDays: String(d.archiveVoidDays || '3'),
        mileageRate: String(d.mileageRate ?? '0.70'),
        xpRoleBase: String(d.xpRoleBase || '50'),
        xpEngagement: String(d.xpEngagement || '1'),
      }));

      // Load per-user notification prefs
      const userId = profile?.id;
      if (userId && d.userPrefs?.[userId]?.notifications) {
        setNotifPrefs((prev) => ({ ...prev, ...d.userPrefs[userId].notifications }));
      }
    }
  }, [settings, profile]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveSettings = async (label = 'Settings') => {
    try {
      const userId = profile?.id;
      const updatedData = {
          ...settings.data,
          // Company
          companyName: formData.companyName,
          companyAddress: formData.companyAddress,
          companyPhone: formData.companyPhone,
          companyEmail: formData.companyEmail,
          companyWeb: formData.companyWeb,
          homebaseAddress: formData.homebaseAddress,
          logoUrl: formData.logoUrl,
          // Appearance
          accentColor: formData.accentColor,
          theme: formData.theme,
          sidebarDefault: formData.sidebarDefault,
          density: formData.density,
          // Quotes
          quotePrefix: formData.quotePrefix,
          quoteStart: parseInt(formData.quoteStart) || 1000,
          depositPct: parseFloat(formData.depositPct) || 50,
          quoteValidity: parseInt(formData.quoteValidity) || 30,
          payTerms: formData.payTerms,
          finalTermsDays: parseInt(formData.finalTermsDays) || 0,
          // Outreach
          yourName: formData.yourName,
          portfolioLink: formData.portfolioLink,
          videoLink: formData.videoLink,
          // Finance
          invoicePrefix: formData.invoicePrefix,
          invoiceStart: parseInt(formData.invoiceStart) || 1,
          splitCompany: parseFloat(formData.splitCompany) || 0,
          splitOriginator: parseFloat(formData.splitOriginator) || 0,
          splitProduction: parseFloat(formData.splitProduction) || 0,
          splitPostProduction: parseFloat(formData.splitPostProduction) || 0,
          archivePaidDays: parseInt(formData.archivePaidDays) || 3,
          archiveVoidDays: parseInt(formData.archiveVoidDays) || 3,
          mileageRate: parseFloat(formData.mileageRate) || 0.70,
          xpRoleBase: parseInt(formData.xpRoleBase) || 50,
          xpEngagement: parseInt(formData.xpEngagement) || 1,
      };
      // Preserve array-typed fields from settings.data only if they're valid arrays;
      // delete them if they somehow became strings (e.g. stale formData)
      if ('quoteTerms' in updatedData && !Array.isArray(updatedData.quoteTerms)) delete updatedData.quoteTerms;
      if ('links' in updatedData && !Array.isArray(updatedData.links)) delete updatedData.links;
      // Save calendar prefs as per-user preference
      if (userId) {
        const existingUserPrefs = settings.data.userPrefs?.[userId] || {};
        const userCalPrefs = { ...existingUserPrefs, calendarView: formData.defaultCalendarView };
        if (formData.defaultCalendarScope) {
          userCalPrefs.calendarScope = formData.defaultCalendarScope;
        } else {
          delete userCalPrefs.calendarScope;
        }
        updatedData.userPrefs = {
          ...(settings.data.userPrefs || {}),
          [userId]: userCalPrefs,
        };
      }
      await updateSettings.mutateAsync({
        ...settings,
        data: updatedData,
      });
      toast(`${label} saved`);
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    }
  };

  // ── Notification prefs ──────────────────────────────────────────────────
  const handleNotifToggle = (key) => {
    setNotifPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSaveNotifications = async () => {
    const userId = profile?.id;
    if (!userId) return;
    try {
      await updateSettings.mutateAsync({
        ...settings,
        data: {
          ...settings.data,
          userPrefs: {
            ...(settings.data.userPrefs || {}),
            [userId]: {
              ...(settings.data.userPrefs?.[userId] || {}),
              notifications: notifPrefs,
            },
          },
        },
      });
      toast('Notification preferences saved');
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    }
  };

  // ── Email template handlers ──────────────────────────────────────────────
  const handleEditTemplate = (templateId) => {
    const customTemplates = settings?.data?.emailTemplates || {};
    const custom = customTemplates[templateId];
    const def = TEMPLATE_TYPES.find((t) => t.id === templateId);
    setEditingTemplateId(templateId);
    setTemplateSubject(custom?.subject || def?.defaultSubject || '');
    setTemplateBody(custom?.body || def?.defaultBody || '');
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplateId) return;
    try {
      const existingTemplates = settings?.data?.emailTemplates || {};
      await updateSettings.mutateAsync({
        ...settings,
        data: {
          ...settings.data,
          emailTemplates: {
            ...existingTemplates,
            [editingTemplateId]: {
              subject: templateSubject,
              body: templateBody,
            },
          },
        },
      });
      toast('Email template saved');
      setEditingTemplateId(null);
    } catch (err) {
      toast(`Error: ${err.message}`, 'error');
    }
  };

  const handleResetTemplate = () => {
    const def = TEMPLATE_TYPES.find((t) => t.id === editingTemplateId);
    if (def) {
      setTemplateSubject(def.defaultSubject);
      setTemplateBody(def.defaultBody);
    }
  };

  if (isLoading) {
    return <div className="text-text-dim text-sm p-4">Loading settings...</div>;
  }

  // ── Tab Navigation ──────────────────────────────────────────────────────
  const allTabs = [
    { id: 'profile', label: 'Profile' },
    { id: 'company', label: 'Company', feature: 'settings', level: 'company' },
    { id: 'appearance', label: 'Appearance' },
    { id: 'quotes', label: 'Quotes', feature: 'settings', level: 'company' },
    { id: 'quoteTerms', label: 'Quote Terms', feature: 'settings', level: 'company' },
    { id: 'finance', label: 'Finance', feature: 'settings', level: 'company' },
    { id: 'emails', label: 'Email Templates', feature: 'settings', level: 'company' },
    { id: 'team', label: 'Team', feature: 'settings', level: 'company' },
    { id: 'roles', label: 'Roles', feature: 'settings', level: 'full' },
    { id: 'notifications', label: 'Notifications', focus: 'settings.notificationsTab' },
    { id: 'integrations', label: 'Integrations', feature: 'settings', level: 'full' },
    { id: 'data', label: 'Data', feature: 'settings', level: 'full' },
    { id: 'trash', label: 'Trash', anyOf: [['jobs', 'full'], ['invoices', 'full']] },
    { id: 'feedback', label: 'Feedback', tool: 'activity' },
    { id: 'activity', label: 'Activity Log', tool: 'activity' },
  ];
  const tabs = allTabs.filter(t => {
    if (t.focus && isHidden(t.focus) && activeTab !== t.id) return false;
    if (t.tool) return hasToolAccess(t.tool, settings);
    if (t.anyOf) return t.anyOf.some(([f, l]) => hasAccess(f, l, settings));
    if (t.feature && t.level) return hasAccess(t.feature, t.level, settings);
    return true;
  });
  // Panels obey the same gate as the tab bar, so ?tab=team (deep link or sidebar
  // flyout) can't open a panel this role isn't allowed to see.
  const shownTab = tabs.some(t => t.id === activeTab) ? activeTab : 'profile';

  const inputCls = 'w-full px-3 py-2 bg-surface border border-border rounded text-text-primary placeholder-text-dim text-sm focus:outline-none focus:border-accent';

  const SaveButton = ({ label, onClick }) => (
    <div className="flex justify-end pt-4">
      <button
        onClick={onClick || (() => handleSaveSettings(label))}
        disabled={updateSettings.isPending}
        className="px-4 py-2 bg-accent text-bg rounded text-sm font-medium hover:bg-accent-hover disabled:opacity-50 transition-colors"
      >
        {updateSettings.isPending ? 'Saving...' : `Save ${label}`}
      </button>
    </div>
  );

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* Tab Navigation */}
      {isMobile ? (
        <select
          value={shownTab}
          onChange={(e) => setActiveTab(e.target.value)}
          className="w-full px-3 py-2 bg-surface border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent"
        >
          {tabs.map((tab) => (
            <option key={tab.id} value={tab.id} data-focus={tab.focus}>{tab.label}</option>
          ))}
        </select>
      ) : (
      <div className="flex gap-1.5 border-b border-border overflow-x-auto pb-px">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            data-focus={tab.focus}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'px-3 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition-colors',
              shownTab === tab.id
                ? 'border-accent text-text-primary'
                : 'border-transparent text-text-dim hover:text-text-primary'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>
      )}

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">

        {/* ═══ PROFILE TAB ═══ */}
        {shownTab === 'profile' && <ProfileTab />}

        {/* ═══ COMPANY TAB ═══ */}
        {shownTab === 'company' && (
          <div className="space-y-4">
            <div className="border border-border/50 rounded p-4 bg-surface-2/50 space-y-3">
              <h4 className="text-sm font-semibold text-text-primary">Company Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-text-dim mb-1">Company Name</label>
                  <input type="text" name="companyName" value={formData.companyName} onChange={handleInputChange} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-dim mb-1">Website</label>
                  <input type="url" name="companyWeb" value={formData.companyWeb} onChange={handleInputChange} className={inputCls} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-text-dim mb-1">Address</label>
                <AddressAutocomplete value={formData.companyAddress} onChange={v => handleInputChange({ target: { name: 'companyAddress', value: v } })} className={inputCls} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-text-dim mb-1">Phone</label>
                  <input type="tel" name="companyPhone" value={formData.companyPhone} onChange={handleInputChange} placeholder="(805) 555-0000" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-dim mb-1">Email</label>
                  <input type="email" name="companyEmail" value={formData.companyEmail} onChange={handleInputChange} placeholder="hello@company.com" className={inputCls} />
                </div>
              </div>
            </div>

            <div className="border border-border/50 rounded p-4 bg-surface-2/50 space-y-3">
              <h4 className="text-sm font-semibold text-text-primary">Homebase Address (S1)</h4>
              <p className="text-2xs text-text-dim">Base of operations for drone flight planning. Used as default departure point.</p>
              <AddressAutocomplete value={formData.homebaseAddress} onChange={v => handleInputChange({ target: { name: 'homebaseAddress', value: v } })} placeholder="570 Bolinas Way, Goleta CA 93117" className={inputCls} />
            </div>

            <div className="border border-border/50 rounded p-4 bg-surface-2/50 space-y-3">
              <h4 className="text-sm font-semibold text-text-primary">Branding</h4>
              {formData.logoUrl && (
                <div className="p-3 bg-surface rounded border border-border inline-block">
                  <img src={formData.logoUrl} alt="Logo preview" className="h-12 max-w-[240px] object-contain" />
                </div>
              )}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const inp = document.createElement('input');
                    inp.type = 'file';
                    inp.accept = 'image/*';
                    inp.onchange = async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 5 * 1024 * 1024) { toast('Image must be under 5 MB', 'error'); return; }
                      try {
                        const fd = new FormData();
                        fd.append('file', file);
                        const res = await fetch('/api/upload/branding', {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                          body: fd,
                        });
                        if (!res.ok) throw new Error('Upload failed');
                        const { url } = await res.json();
                        setFormData(prev => ({ ...prev, logoUrl: url }));
                        toast('Logo uploaded — save to apply');
                      } catch (err) {
                        toast(`Error: ${err.message}`, 'error');
                      }
                    };
                    inp.click();
                  }}
                  className="px-3 py-2 bg-surface border border-border text-text-primary rounded text-sm hover:bg-surface-2 transition-colors"
                >
                  {formData.logoUrl ? 'Change Logo' : 'Upload Logo'}
                </button>
                {formData.logoUrl && (
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, logoUrl: '' }))}
                    className="text-2xs text-text-dim hover:text-red-accent"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>

            <SaveButton label="Company Settings" />
          </div>
        )}

        {/* ═══ APPEARANCE TAB ═══ */}
        {shownTab === 'appearance' && (
          <div className="space-y-4">
            {!isHidden('settings.deadControls') && (
              <div data-focus="settings.deadControls" className="border border-border/50 rounded p-4 bg-surface-2/50 space-y-3">
                <h4 className="text-sm font-semibold text-text-primary">Layout</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-text-dim mb-1">Sidebar Default</label>
                    <select name="sidebarDefault" value={formData.sidebarDefault} onChange={handleInputChange} className={inputCls}>
                      <option value="expanded">Expanded</option>
                      <option value="collapsed">Collapsed (icons only)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text-dim mb-1">Density</label>
                    <select name="density" value={formData.density} onChange={handleInputChange} className={inputCls}>
                      <option value="comfortable">Comfortable</option>
                      <option value="compact">Compact</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            <div className="border border-border/50 rounded p-4 bg-surface-2/50 space-y-3">
              <h4 className="text-sm font-semibold text-text-primary">Calendar Preferences</h4>
              <p className="text-xs text-text-dim">Your personal defaults when opening the calendar.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-text-dim mb-1">Default View</label>
                  <select name="defaultCalendarView" value={formData.defaultCalendarView} onChange={handleInputChange} className={inputCls}>
                    <option value="week">Week</option>
                    <option value="month">Month</option>
                    <option value="year">Year</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-dim mb-1">Default Scope</label>
                  <select name="defaultCalendarScope" value={formData.defaultCalendarScope} onChange={handleInputChange} className={inputCls}>
                    <option value="">Role default</option>
                    <option value="mine">My Cal</option>
                    <option value="company">Company</option>
                  </select>
                </div>
              </div>
            </div>

            <SaveButton label="Appearance" />
          </div>
        )}

        {/* ═══ QUOTES TAB ═══ */}
        {shownTab === 'quotes' && (
          <div className="space-y-4">
            <div className="border border-border/50 rounded p-4 bg-surface-2/50 space-y-3">
              <h4 className="text-sm font-semibold text-text-primary">Quote Defaults</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-text-dim mb-1">Deposit %</label>
                  <input type="number" name="depositPct" value={formData.depositPct} onChange={handleInputChange} min="0" max="100" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-dim mb-1">Quote Validity (days)</label>
                  <input type="number" name="quoteValidity" value={formData.quoteValidity} onChange={handleInputChange} min="1" className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-text-dim mb-1">Payment Terms</label>
                  <input type="text" name="payTerms" value={formData.payTerms} onChange={handleInputChange} placeholder="e.g., Net 30" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-dim mb-1">Final Invoice Terms (days)</label>
                  <input type="number" name="finalTermsDays" value={formData.finalTermsDays} onChange={handleInputChange} min="0" placeholder="0" className={inputCls} />
                  <p className="text-2xs text-text-dim mt-1">0 = due on receipt</p>
                </div>
              </div>
            </div>

            <div className="border border-border/50 rounded p-4 bg-surface-2/50 space-y-3">
              <h4 className="text-sm font-semibold text-text-primary">Terms & Conditions</h4>
              <p className="text-2xs text-text-dim">
                Edit the terms library (default bodies + custom clauses everyone gets) in the{' '}
                <button
                  type="button"
                  onClick={() => setActiveTab('quoteTerms')}
                  className="text-accent hover:underline"
                >
                  Quote Terms tab
                </button>
                . Per-quote overrides happen inside each quote builder.
              </p>
            </div>

            <SaveButton label="Quote Settings" />
          </div>
        )}

        {/* ═══ FINANCE TAB ═══ */}
        {shownTab === 'finance' && (
          <div className="space-y-4">
            <div className="border border-border/50 rounded p-4 bg-surface-2/50 space-y-3">
              <h4 className="text-sm font-semibold text-text-primary">Default Revenue Splits</h4>
              <p className="text-2xs text-text-dim">Default starting percentages for the revenue split calculator. Adjustable per job.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-text-dim mb-1">Company Share %</label>
                  <input type="number" name="splitCompany" value={formData.splitCompany} onChange={handleInputChange} step="0.1" min="0" max="100" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-dim mb-1">Originator Share %</label>
                  <input type="number" name="splitOriginator" value={formData.splitOriginator} onChange={handleInputChange} step="0.1" min="0" max="100" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-dim mb-1">Production Share %</label>
                  <input type="number" name="splitProduction" value={formData.splitProduction} onChange={handleInputChange} step="0.1" min="0" max="100" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-dim mb-1">Post Production Share %</label>
                  <input type="number" name="splitPostProduction" value={formData.splitPostProduction} onChange={handleInputChange} step="0.1" min="0" max="100" className={inputCls} />
                </div>
              </div>
              {(() => {
                const splitSum = (parseFloat(formData.splitCompany) || 0) + (parseFloat(formData.splitOriginator) || 0) + (parseFloat(formData.splitProduction) || 0) + (parseFloat(formData.splitPostProduction) || 0);
                if (Math.abs(splitSum - 100) > 0.5) {
                  return <p className="text-xs text-red-accent font-medium mt-2">⚠ Splits total {splitSum.toFixed(1)}% — must equal 100%</p>;
                }
                return null;
              })()}
            </div>

            <div className="border border-border/50 rounded p-4 bg-surface-2/50 space-y-3">
              <h4 className="text-sm font-semibold text-text-primary">Auto-Archive</h4>
              <p className="text-2xs text-text-dim">Automatically archive paid/void jobs after the specified number of days.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-text-dim mb-1">Paid Jobs (days)</label>
                  <input type="number" name="archivePaidDays" value={formData.archivePaidDays} onChange={handleInputChange} min="1" max="365" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-dim mb-1">Void Jobs (days)</label>
                  <input type="number" name="archiveVoidDays" value={formData.archiveVoidDays} onChange={handleInputChange} min="1" max="365" className={inputCls} />
                </div>
              </div>
            </div>

            <div className="border border-border/50 rounded p-4 bg-surface-2/50 space-y-3">
              <h4 className="text-sm font-semibold text-text-primary">Mileage Reimbursement</h4>
              <p className="text-2xs text-text-dim">Rate paid per mile driven on Travel time entries (driver only).</p>
              <div>
                <label className="block text-xs font-medium text-text-dim mb-1">$ per mile</label>
                <input type="number" step="0.01" min="0" name="mileageRate" value={formData.mileageRate} onChange={handleInputChange} className={inputCls} />
              </div>
            </div>

            <div className="border border-border/50 rounded p-4 bg-surface-2/50 space-y-3">
              <h4 className="text-sm font-semibold text-text-primary">XP / Gamification</h4>
              <p className="text-2xs text-text-dim">Configure experience points awarded to crew and contacts.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-text-dim mb-1">Role XP Base (per job)</label>
                  <input type="number" name="xpRoleBase" value={formData.xpRoleBase} onChange={handleInputChange} min="1" max="1000" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-dim mb-1">Engagement XP (per action)</label>
                  <input type="number" name="xpEngagement" value={formData.xpEngagement} onChange={handleInputChange} min="1" max="100" className={inputCls} />
                </div>
              </div>
            </div>

            <SaveButton label="Finance Settings" />
          </div>
        )}

        {/* ═══ EMAIL TEMPLATES TAB ═══ */}
        {shownTab === 'emails' && (
          <div className="space-y-4">
            <p className="text-text-dim text-sm">
              Customize email templates used by the "Send Email" buttons. Use merge tags like{' '}
              <code className="text-accent text-xs">{'{{clientName}}'}</code> for dynamic content.
            </p>

            {editingTemplateId ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-text-primary">
                    {TEMPLATE_TYPES.find((t) => t.id === editingTemplateId)?.label}
                  </h3>
                  <button onClick={() => setEditingTemplateId(null)} className="text-2xs text-text-dim hover:text-text-primary">
                    ← Back to list
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Subject</label>
                  <input type="text" value={templateSubject} onChange={(e) => setTemplateSubject(e.target.value)} className={inputCls} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">Body</label>
                  <textarea value={templateBody} onChange={(e) => setTemplateBody(e.target.value)} rows={12} className={`${inputCls} font-mono`} />
                </div>

                <div className="bg-surface border border-border rounded p-3">
                  <h4 className="text-2xs font-semibold text-text-dim uppercase mb-2">Available Merge Tags</h4>
                  <div className="grid grid-cols-2 gap-1">
                    {MERGE_TAGS.map((tag) => (
                      <div key={tag.tag} className="text-2xs">
                        <code className="text-accent">{tag.tag}</code>
                        <span className="text-text-dim ml-1">— {tag.description}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    onClick={handleSaveTemplate}
                    disabled={updateSettings.isPending}
                    className="px-4 py-2 bg-accent text-bg rounded text-sm font-medium hover:bg-accent-hover disabled:opacity-50 transition-colors"
                  >
                    {updateSettings.isPending ? 'Saving...' : 'Save Template'}
                  </button>
                  <button
                    onClick={handleResetTemplate}
                    className="px-4 py-2 bg-surface border border-border text-text-primary rounded text-sm hover:bg-surface-2 transition-colors"
                  >
                    Reset to Default
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {TEMPLATE_TYPES.map((template) => {
                  const customTemplates = settings?.data?.emailTemplates || {};
                  const isCustomized = !!customTemplates[template.id];
                  return (
                    <div
                      key={template.id}
                      onClick={() => handleEditTemplate(template.id)}
                      className="card p-3 flex items-center justify-between cursor-pointer hover:border-accent/40 transition-colors"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-text-primary">{template.label}</span>
                          {isCustomized && (
                            <span className="text-2xs px-1.5 py-0.5 rounded bg-accent/10 text-accent border border-accent/30">Customized</span>
                          )}
                        </div>
                        <p className="text-2xs text-text-dim mt-0.5">{template.description}</p>
                      </div>
                      <span className="text-text-dim text-sm">→</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ═══ QUOTE TERMS TAB (library editor) ═══ */}
        {shownTab === 'quoteTerms' && <QuoteTermsTab />}
        {/* ═══ TEAM TAB ═══ */}
        {shownTab === 'team' && <TeamTab />}

        {/* ═══ ROLES TAB ═══ */}
        {shownTab === 'roles' && <RoleMatrixBuilder />}

        {/* ═══ NOTIFICATIONS TAB ═══ */}
        {shownTab === 'notifications' && (
          <div className="space-y-4">
            <p className="text-text-dim text-sm">Configure when you receive email notifications. These preferences are per-user.</p>

            <div className="border border-border/50 rounded p-4 bg-surface-2/50 space-y-3">
              <h4 className="text-sm font-semibold text-text-primary">Email Notifications</h4>
              {[
                { key: 'emailJobAssigned', label: 'Job Assigned', desc: 'When a job is assigned to you' },
                { key: 'emailInvoicePaid', label: 'Invoice Paid', desc: 'When a client pays an invoice' },
                { key: 'emailQuoteAccepted', label: 'Quote Accepted', desc: 'When a client accepts a quote' },
                { key: 'emailPaymentReceived', label: 'Payment Received', desc: 'When any payment is recorded' },
              ].map((item) => (
                <label key={item.key} className="flex items-start gap-3 cursor-pointer hover:bg-surface p-2 rounded transition-colors">
                  <input
                    type="checkbox"
                    checked={!!notifPrefs[item.key]}
                    onChange={() => handleNotifToggle(item.key)}
                    className="mt-1 w-4 h-4"
                  />
                  <div>
                    <div className="text-sm font-medium text-text-primary">{item.label}</div>
                    <div className="text-2xs text-text-dim">{item.desc}</div>
                  </div>
                </label>
              ))}
            </div>

            <div className="border border-border/50 rounded p-4 bg-surface-2/50 space-y-3">
              <h4 className="text-sm font-semibold text-text-primary">Digest</h4>
              {[
                { key: 'emailDailyDigest', label: 'Daily Digest', desc: 'Summary of activity every morning' },
                { key: 'emailWeeklyDigest', label: 'Weekly Digest', desc: 'Summary of activity every Monday' },
              ].map((item) => (
                <label key={item.key} className="flex items-start gap-3 cursor-pointer hover:bg-surface p-2 rounded transition-colors">
                  <input
                    type="checkbox"
                    checked={!!notifPrefs[item.key]}
                    onChange={() => handleNotifToggle(item.key)}
                    className="mt-1 w-4 h-4"
                  />
                  <div>
                    <div className="text-sm font-medium text-text-primary">{item.label}</div>
                    <div className="text-2xs text-text-dim">{item.desc}</div>
                  </div>
                </label>
              ))}
            </div>

            <SaveButton label="Notifications" onClick={handleSaveNotifications} />
          </div>
        )}

        {/* ═══ INTEGRATIONS TAB ═══ */}
        {shownTab === 'integrations' && <IntegrationsTab />}

        {/* ═══ DATA TAB ═══ */}
        {shownTab === 'data' && <DataTab />}

        {/* ═══ TRASH TAB ═══ */}
        {shownTab === 'trash' && <TrashTab />}

        {/* ═══ FEEDBACK TAB ═══ */}
        {shownTab === 'feedback' && <FeedbackTab />}

        {/* ═══ ACTIVITY LOG TAB ═══ */}
        {shownTab === 'activity' && <ActivityLogTab />}
      </div>
    </div>
  );
}

/** Inline Activity Log tab with search */
function ActivityLogTab() {
  const [search, setSearch] = useState('');
  return (
    <div className="flex flex-col gap-3" style={{ flex: 1, minHeight: 0 }}>
      <input
        type="text"
        placeholder="Search by user, action, job..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full px-3 py-2 bg-surface border border-border rounded text-text-primary placeholder-text-dim text-sm focus:outline-none focus:border-accent"
      />
      <div className="flex-1 min-h-0">
        <ActionLog search={search} />
      </div>
    </div>
  );
}
